import { useEffect, useState } from "react";
import { Button, Col, DatePicker, Input, Row, Select } from "antd";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { GlobalTable } from "../../../global/components/GlobalTable";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import useGet from "../../../global/hooks/useGet";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { plateAccessTypeOptions } from "../../../utils/Common";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import { PrinterOutlined, SaveOutlined } from "@ant-design/icons";
import { CallHistoryColumns } from "./Columns";
import {
  downloadCallHistoryPDF,
  printCallHistoryPDF,
} from "../utils/downloadCallHistoryPdf";

const unlockedFromOptions = [
  { value: 0, label: "Virtual Intercom" },
  { value: 1, label: "Directory" },
];

const accessResultOptions = [
  { value: 1, label: "Granted" },
  { value: 0, label: "Denied" },
];

const initialFilterState = {
  PersonName: "",
  AccessItemName: "",
  AccessType: null,
  ControlFrom: null,
  AccessGranted: null,
  DeviceName: "",
  DeviceNo: "",
  LogTime: null,
};

const CALL_HISTORY_EXPORT_URL = `${process.env.REACT_APP_API_URL}/api/plates/call-logs/export/`;

export const CallHistory = () => {
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "CallLogNo",
    order: "desc",
  });
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {};

  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [filter, setFilter] = useState(initialFilterState);
  const [exportLoading, setExportLoading] = useState(false);

  const query = new URLSearchParams(params);
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();

  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/plates/call-logs/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/plates/call-logs/`;
  const { data, loading, refetch } = useGet(url);

  useEffect(() => {
    const matchedFilter = { ...initialFilterState };
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined && modParams[key] !== "") {
        if (key === "LogTime") {
          matchedFilter[key] = dayjs(String(modParams[key]), "YYYY-MM");
        } else if (
          key === "AccessType" ||
          key === "ControlFrom" ||
          key === "AccessGranted"
        ) {
          matchedFilter[key] = Number(modParams[key]);
        } else {
          matchedFilter[key] = modParams[key];
        }
      }
    }
    setFilter(matchedFilter);
  }, []);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.CallLogNo,
      }));
      setListData(newData || []);
    }
  }, [data, loading]);

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize,
      });
      setParams({
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page,
      });
    },
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""']/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = () => {
    const filterDict = {};
    for (const key in filter) {
      const value = filter[key];
      if (value !== null && value !== "" && value !== undefined) {
        if (key === "LogTime") {
          filterDict[key] = dayjs.isDayjs(value)
            ? value.format("YYYY-MM")
            : dayjs(value).format("YYYY-MM");
        } else {
          filterDict[key] = value;
        }
      }
    }

    if (Object.keys(filterDict).length === 0) {
      handleReset();
      return;
    }

    setPagination((prev) => ({ ...prev, current: 1 }));
    setParams({
      limit: 10,
      sort_by: "CallLogNo",
      order: "desc",
      page: 1,
      ...filterDict,
    });
    refetch();
  };

  const handleReset = () => {
    setFilter({ ...initialFilterState });
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    setParams({
      limit: 10,
      sort_by: "CallLogNo",
      order: "desc",
    });
    refetch();
  };

  const getActiveFilterParams = () => {
    const exportParams = { ...Object.fromEntries([...params.entries()]) };
    delete exportParams.page;
    delete exportParams.limit;
    delete exportParams.pageSize;
    delete exportParams.total;
    delete exportParams.current;
    return exportParams;
  };

  const fetchExportRows = async () => {
    const token = JSON.parse(sessionStorage.getItem("userData"));
    const accessToken = token?.access_token;
    const exportParams = getActiveFilterParams();
    const response = await axios.get(CALL_HISTORY_EXPORT_URL, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      params: exportParams,
    });
    return {
      rows: response?.data?.data || [],
      filterParams: exportParams,
    };
  };

  const handleSavePdf = async () => {
    try {
      setExportLoading(true);
      const { rows, filterParams } = await fetchExportRows();
      if (!rows.length) {
        openErrorNotification("No call history found to save.");
        return;
      }
      const timestamp = new Date().toISOString().split("T")[0];
      downloadCallHistoryPDF(
        rows,
        filterParams,
        `Call-History_${timestamp}.pdf`,
      );
      openConfirmNotification("Success");
    } catch (error) {
      openErrorNotification(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save PDF.",
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handlePrintPdf = async () => {
    try {
      setExportLoading(true);
      const { rows, filterParams } = await fetchExportRows();
      if (!rows.length) {
        openErrorNotification("No call history found to print.");
        return;
      }
      printCallHistoryPDF(rows, filterParams);
      openConfirmNotification("Success");
    } catch (error) {
      openErrorNotification(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to print PDF.",
      );
    } finally {
      setExportLoading(false);
    }
  };

  const breadcrumbItems = [
    {
      label: "Call History",
      href: "/call-history",
    },
  ];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <div className="mb-4">
          <div className="mt-2">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Input
                  className="input_text_3 mr10 h-full"
                  placeholder="Answered By"
                  value={filter.PersonName}
                  onChange={handleFilterChange}
                  name="PersonName"
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Input
                  className="input_text_3 mr10 h-full"
                  placeholder="Access Item"
                  value={filter.AccessItemName}
                  onChange={handleFilterChange}
                  name="AccessItemName"
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Select
                  showSearch
                  placeholder="Access Type"
                  optionFilterProp="label"
                  value={
                    filter.AccessType === 0 || filter.AccessType
                      ? Number(filter.AccessType)
                      : undefined
                  }
                  onChange={(value) =>
                    setFilter((prev) => ({
                      ...prev,
                      AccessType: value ?? null,
                    }))
                  }
                  options={plateAccessTypeOptions}
                  style={{ width: "100%" }}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Select
                  showSearch
                  placeholder="Unlocked From"
                  optionFilterProp="label"
                  value={
                    filter.ControlFrom === 0 || filter.ControlFrom
                      ? Number(filter.ControlFrom)
                      : undefined
                  }
                  onChange={(value) =>
                    setFilter((prev) => ({
                      ...prev,
                      ControlFrom: value ?? null,
                    }))
                  }
                  options={unlockedFromOptions}
                  style={{ width: "100%" }}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Select
                  showSearch
                  placeholder="Access"
                  optionFilterProp="label"
                  value={
                    filter.AccessGranted === 0 || filter.AccessGranted
                      ? Number(filter.AccessGranted)
                      : undefined
                  }
                  onChange={(value) =>
                    setFilter((prev) => ({
                      ...prev,
                      AccessGranted: value ?? null,
                    }))
                  }
                  options={accessResultOptions}
                  style={{ width: "100%" }}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Input
                  className="input_text_3 mr10 h-full"
                  placeholder="Device Name"
                  value={filter.DeviceName}
                  onChange={handleFilterChange}
                  name="DeviceName"
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Input
                  className="input_text_3 mr10 h-full"
                  placeholder="Device No"
                  value={filter.DeviceNo}
                  name="DeviceNo"
                  onChange={(e) => {
                    const { value } = e.target;
                    if (/^\d*$/.test(value)) {
                      handleFilterChange(e);
                    }
                  }}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <DatePicker
                  picker="month"
                  placeholder="Month"
                  format="YYYY-MM"
                  value={filter.LogTime}
                  onChange={(value) =>
                    setFilter((prev) => ({
                      ...prev,
                      LogTime: value ?? null,
                    }))
                  }
                  style={{ width: "100%", height: "32px" }}
                  allowClear
                />
              </Col>
            </Row>
          </div>

          <div className="w-full overflow-x-auto">
            <div className="flex justify-between my-4 flex-nowrap min-w-[420px]">
              <div className="flex items-center">
                <Button
                  type="primary"
                  className="mr-2 w-[104px] h-[30px] font-medium"
                  onClick={handleFilter}
                >
                  <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                  Apply
                </Button>
                <Button className="reset-button" onClick={handleReset}>
                  <HiArrowPath className="!text-[14px] font-medium" />
                  Reset
                </Button>
              </div>
              <div className="flex items-center">
                <Button
                  type="primary"
                  className="mr-2 w-[104px] h-[30px] font-medium"
                  onClick={handleSavePdf}
                  loading={exportLoading}
                >
                  <SaveOutlined className="!text-[14px] font-medium" />
                  Save
                </Button>
                <Button
                  type="primary"
                  className="w-[104px] h-[30px] font-medium"
                  onClick={handlePrintPdf}
                  loading={exportLoading}
                >
                  <PrinterOutlined className="!text-[14px] font-medium" />
                  Print
                </Button>
              </div>
            </div>
          </div>
        </div>

        <GlobalTable
          columns={CallHistoryColumns(setParams, params)}
          dataSource={listData}
          loading={loading}
          pagination={paginationConfig}
          resize
        />
      </div>
    </div>
  );
};
